import React, { useState, useRef, useCallback, useEffect } from 'react';
import { QRCodeSVG, QRCodeCanvas } from 'qrcode.react';
import { Case, QRPoint, RiskLevel, Team, PLAN_CONFIGS, ResolutionActionTag, Category, OrgMember, OrgInvite, priceForSeats, SEAT_PRICE, MAX_SELF_SERVE_SEATS } from '../types';
import { useAppStore } from '../store/useAppStore';
import { riskLabel, statusLabel } from '../utils/caseLabels';
import { openCustomerPortal, isStripeConfigured, createCheckoutSession } from '../services/stripe';
import {
  getOrganization, updateOrganizationName, saveCaseResolution,
  getCurrentUser, getUserSubscription,
  getOrgMembers, getPendingInvites, inviteMember, revokeInvite, removeMember,
  createTeam, getTeams, archiveTeam, unarchiveTeam, renameTeam,
  updateSeats, updateDefaultTeam, setQRPointTeam,
} from '../services/supabase';
import { logger } from '../utils/logger';
import { isLimitExceededCase, filterRealCases, filterLimitExceededCases, hasWhyAnalysis, splitLessonAndWhy, splitLessonByLanguage, parseWhyAnalysis, type ParsedWhyAnalysis } from '../utils/caseClassification';
import {
  Printer,
  Plus,
  Download,
  Trash2,
  CreditCard,
  Check,
  ExternalLink,
  Loader2,
  X,
  AlertCircle,
  AlertTriangle,
  Edit3,
  ToggleLeft,
  ToggleRight,
  Copy,
  CheckCircle,
  Save,
  Building2,
  Image as ImageIcon,
  Camera,
  ChevronDown,
  ChevronUp,
  Search,
  ArrowUpRight,
  MapPin
} from 'lucide-react';

// Format date to Japanese timezone (JST) with readable format
const formatDateTimeJST = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleString('ja-JP', {
      timeZone: 'Asia/Tokyo',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return '-';
  }
};

// ============ Cases View ============

interface ManagerCasesViewProps {
  cases: Case[];
  qrPoints?: QRPoint[];
  onUpdateCase?: (id: string, updates: Partial<Case>) => void;
  /** Dashboard の Hero 等から「リスク」を絞った状態で開かせる時に渡す */
  initialRiskFilter?: RiskLevel | 'all';
  /** Dashboard の Hero 等から「ステータス」を絞った状態で開かせる時に渡す */
  initialStatusFilter?: Case['status'] | 'all';
  /** 「このケースを深掘り分析」(4Mなぜなぜ) を呼ぶ. App.tsx 側で /why-demo に遷移 + preset 投入. */
  onDeepAnalyze?: (caseBody: string, caseId: string) => void;
  /** 0件初回時のCTA: QR管理画面へ */
  onGoToQR?: () => void;
}

export const ManagerCasesView: React.FC<ManagerCasesViewProps> = ({
  cases,
  qrPoints = [],
  onUpdateCase,
  initialRiskFilter = 'all',
  initialStatusFilter = 'all',
  onDeepAnalyze,
  onGoToQR,
}) => {
  const [filterRisk, setFilterRisk] = useState<RiskLevel | 'all'>(initialRiskFilter);
  const [filterStatus, setFilterStatus] = useState<Case['status'] | 'all'>(initialStatusFilter);
  const [selectedQRId, setSelectedQRId] = useState<string | 'all'>('all');
  const [selectedCase, setSelectedCase] = useState<Case | null>(null);
  // 通常ケース表示 / AI 上限到達ケース表示 のタブ切替
  const [casesTab, setCasesTab] = useState<'real' | 'limit_exceeded'>('real');

  // 上限到達ケースを通常運用から分離する. ベース母集団自体を絞り込む.
  const realCases = filterRealCases(cases);
  const limitExceededCases = filterLimitExceededCases(cases);
  const baseCases = casesTab === 'limit_exceeded' ? limitExceededCases : realCases;

  // Get case counts per QR point (通常ケースのみで集計)
  const qrCaseCounts = qrPoints.reduce((acc, qr) => {
    acc[qr.id] = realCases.filter(c => c.qrPointId === qr.id).length;
    return acc;
  }, {} as Record<string, number>);

  // Count cases without QR point
  const noQrCaseCount = realCases.filter(c => !c.qrPointId).length;

  /* 工事現場での絞り込み。既定はユーザーが選んだ現場（users.default_team_id）。
     'all' はすべての現場、'none' は現場を指定していない報告。 */
  const teams = useAppStore(s => s.teams);
  const currentUser = useAppStore(s => s.user);
  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    () => currentUser?.defaultTeamId || 'all',
  );

  /* 「1件も報告が無い初回」と「フィルタで0件」を区別する。
     以前はどちらも「選択したフィルターに一致するケースがありません」と出ていたため、
     1件も無い初回のユーザーがフィルタのせいだと誤解していた。 */
  const isFirstRun = casesTab === 'real' && realCases.length === 0;
  const noFilterActive = filterRisk === 'all' && filterStatus === 'all'
    && selectedQRId === 'all' && selectedTeamId === 'all';

  /* 「フィルターを解除する」は**現場も戻す**。以前は現場だけ残り、
     解除したのに件数が変わらないという状態になっていた。
     保存済みの既定現場も外す——外さないと、解除した直後に
     リロードすると絞り込みが復活して「解除できていない」ように見える。 */
  const clearFilters = useCallback(() => {
    setFilterRisk('all');
    setFilterStatus('all');
    setSelectedQRId('all');
    if (selectedTeamId !== 'all') {
      setSelectedTeamId('all');
      void updateDefaultTeam(null);
    }
  }, [selectedTeamId]);

  const filteredCases = baseCases.filter(c => {
    // 工事現場フィルタ
    if (selectedTeamId === 'none' && c.teamId) return false;
    if (selectedTeamId !== 'all' && selectedTeamId !== 'none' && c.teamId !== selectedTeamId) return false;
    // QR filter
    if (selectedQRId === 'none' && c.qrPointId) return false;
    if (selectedQRId !== 'all' && selectedQRId !== 'none' && c.qrPointId !== selectedQRId) return false;
    // Risk filter (上限到達タブでは risk フィルタは無効化されているので影響なし)
    if (filterRisk !== 'all' && c.riskLevel !== filterRisk) return false;
    // Status filter
    if (filterStatus !== 'all' && c.status !== filterStatus) return false;
    return true;
  });

  // 変換の実体は utils/caseLabels.ts に集約（管理画面と報告者画面で表記がずれていた）
  const getStatusLabel = (status: Case['status']) => statusLabel(status);

  const getStatusColor = (status: Case['status']) => {
    switch (status) {
      case 'open': return 'bg-red-100 text-red-800';
      case 'in_progress': return 'bg-yellow-100 text-yellow-800';
      case 'resolved': return 'bg-green-100 text-green-800';
    }
  };

  // 変換の実体は utils/caseLabels.ts に集約（同じ変換が各所に散っていたため）
  const getRiskLabel = (risk: RiskLevel) => riskLabel(risk);

  /* Category enum の実値は 'Man (人)' 形式。以前は小文字キー（'man' 等）のマップだけを
     持っていたため一度も一致せず、CSVには英語まじりの生値が出ていた。
     旧データが小文字で入っている可能性に備えて両方受ける。 */
  const getCategoryLabel = (category: string) => {
    const categoryMap: Record<string, string> = {
      [Category.MAN]: '人',
      [Category.MACHINE]: '機械',
      [Category.MATERIAL]: '材料',
      [Category.METHOD]: '方法',
      'man': '人',
      'machine': '機械',
      'material': '材料',
      'method': '方法',
    };
    return categoryMap[category] || category;
  };

  const handleExportCSV = useCallback(() => {
    if (filteredCases.length === 0) {
      alert('エクスポートするケースがありません。');
      return;
    }

    // CSV header
    /* 現場列を入れる。入れないと Excel に落とした時点で現場の区別が消え、
       現場ごとに集計するという機能の目的そのものが失われる。 */
    const headers = ['日時', '工事現場', 'タイトル', '内容', 'リスクレベル', 'カテゴリ', 'ステータス', '音声'];

    // CSV rows
    /* Case に `category` というフィールドは無い（`category1` / `category2`）。
       以前は `c.category` を渡していたため、カテゴリ列が全行 undefined になっていた。
       あわせて全セルをクォートする。ラベルにカンマが入ると列がずれるため。 */
    const csvCell = (v: unknown) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const rows = filteredCases.map(c => [
      csvCell(formatDateTimeJST(c.createdAt)),
      csvCell(teams.find((t) => t.id === c.teamId)?.name ?? ''),
      csvCell(c.title || ''),
      csvCell(c.body || ''),
      csvCell(getRiskLabel(c.riskLevel)),
      csvCell(getCategoryLabel(c.category1)),
      csvCell(getStatusLabel(c.status)),
      csvCell(c.hasAudio ? 'あり' : 'なし')
    ]);

    // Combine header and rows
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    // Add BOM for Excel compatibility
    const bom = '\uFEFF';
    const blob = new Blob([bom + csvContent], { type: 'text/csv;charset=utf-8;' });

    // Create download link
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const now = new Date();
    const filename = `cases_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [filteredCases, teams]);

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-2xl font-bold text-industrial-900">ケース一覧</h2>
        <div className="flex flex-wrap gap-2">
          {/* 上限到達ケースのタブ切替 (件数があるときだけ表示) */}
          {limitExceededCases.length > 0 && (
            <div className="inline-flex bg-slate-100 rounded-lg p-0.5 mr-1">
              <button
                onClick={() => setCasesTab('real')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition ${
                  casesTab === 'real' ? 'bg-white text-industrial-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
                }`}
              >
                通常ケース ({realCases.length})
              </button>
              <button
                onClick={() => setCasesTab('limit_exceeded')}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition flex items-center gap-1 ${
                  casesTab === 'limit_exceeded' ? 'bg-white text-amber-700 shadow-sm' : 'text-slate-600 hover:text-amber-700'
                }`}
              >
                <AlertCircle size={14} />
                AI未分析 ({limitExceededCases.length})
              </button>
            </div>
          )}
          {/* 工事現場のフィルタ。現場が1つも無い組織では出さない
              （既存の見た目を変えないため）。終了した現場も選べるようにする——
              過去の報告への到達性を消さない。 */}
          {teams.length > 0 && (
            <select
              value={selectedTeamId}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedTeamId(v);
                /* 選んだ現場を保存する。事務所のPCと現場のスマホで
                   選び直さずに済むよう DB に持つ（users.default_team_id）。
                   'none'（現場の指定なし）は一時的な絞り込みなので保存しない。 */
                if (v === 'all' || v === 'none') {
                  if (v === 'all') void updateDefaultTeam(null);
                } else {
                  void updateDefaultTeam(v);
                }
              }}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-safety-500 focus:border-transparent"
            >
              <option value="all">すべての現場</option>
              {teams.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}{t.archivedAt ? '（終了）' : ''}
                </option>
              ))}
              <option value="none">現場の指定なし</option>
            </select>
          )}
          <select
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value as RiskLevel | 'all')}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-safety-500 focus:border-transparent"
          >
            <option value="all">すべてのリスク</option>
            <option value={RiskLevel.HIGH}>高</option>
            <option value={RiskLevel.MEDIUM}>中</option>
            <option value={RiskLevel.LOW}>低</option>
          </select>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as Case['status'] | 'all')}
            className="border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-safety-500 focus:border-transparent"
          >
            <option value="all">すべてのステータス</option>
            <option value="open">未対応</option>
            <option value="in_progress">対応中</option>
            <option value="resolved">対応済み</option>
          </select>
          <button
            onClick={handleExportCSV}
            className="bg-industrial-900 text-white px-4 py-2 rounded-lg text-sm hover:bg-slate-800 transition flex items-center gap-2"
          >
            <Download size={16} />
            CSVエクスポート
          </button>
        </div>
      </div>

      {/* QR Point Tabs */}
      {qrPoints.length > 0 && (
        <div className="flex overflow-x-auto gap-1 pb-2 -mb-2 scrollbar-hide">
          <button
            onClick={() => setSelectedQRId('all')}
            className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
              selectedQRId === 'all'
                ? 'bg-industrial-900 text-white shadow-sm'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            すべて
            <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
              selectedQRId === 'all' ? 'bg-white/20' : 'bg-slate-200'
            }`}>
              {baseCases.length}
            </span>
          </button>
          {qrPoints.map(qr => (
            <button
              key={qr.id}
              onClick={() => setSelectedQRId(qr.id)}
              className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                selectedQRId === qr.id
                  ? 'bg-industrial-900 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {qr.name}
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                selectedQRId === qr.id ? 'bg-white/20' : 'bg-slate-200'
              }`}>
                {qrCaseCounts[qr.id] || 0}
              </span>
            </button>
          ))}
          {noQrCaseCount > 0 && (
            <button
              onClick={() => setSelectedQRId('none')}
              className={`flex-shrink-0 px-4 py-2.5 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                selectedQRId === 'none'
                  ? 'bg-industrial-900 text-white shadow-sm'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              QRなし
              <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                selectedQRId === 'none' ? 'bg-white/20' : 'bg-slate-200'
              }`}>
                {noQrCaseCount}
              </span>
            </button>
          )}
        </div>
      )}

      {/* 一覧の使い方ヒント: 「クリックで詳細・対策入力に進める」ことを明示 */}
      {casesTab === 'real' && filteredCases.length > 0 && (
        <div className="hidden md:block bg-safety-50 border border-safety-200 rounded-lg px-4 py-2.5 mb-3 text-sm text-safety-800 flex items-center gap-2">
          <span aria-hidden>👆</span>
          <span>各ケースをクリックすると、<strong>状況確認・対策入力</strong>のページが開きます。</span>
        </div>
      )}

      {/* AI 未分析 (上限到達) タブ: 説明 + アップグレード CTA */}
      {casesTab === 'limit_exceeded' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 md:p-5 mb-3">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-bold text-amber-900 mb-1">
                AI分析の月次上限を超えた {limitExceededCases.length} 件
              </h3>
              <p className="text-xs text-amber-800 leading-relaxed">
                投稿された原文はすべて保存されています。AI による4M分類・リスク判定だけが行われていません。<br />
                プランをアップグレードすると当月の上限が広がり、現場の声を漏らさず分析できます。
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <a
                  href="/pricing"
                  className="inline-flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition"
                >
                  プランをアップグレード →
                </a>
                <a
                  href="https://anzenpost.com/pricing"
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1.5 bg-white text-amber-800 border border-amber-300 text-xs font-bold px-4 py-2 rounded-lg hover:bg-amber-50 transition"
                >
                  料金プランを見る
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Desktop: table view */}
      <div className="hidden md:block bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase font-semibold">
              <tr>
                <th className="px-6 py-4">日付</th>
                <th className="px-6 py-4">タイトル / 内容</th>
                <th className="px-6 py-4">リスク</th>
                <th className="px-6 py-4">カテゴリ</th>
                <th className="px-6 py-4">ステータス</th>
                <th className="px-6 py-4 text-center">写真</th>
                <th className="px-6 py-4 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredCases.map((c) => (
                <tr
                  key={c.id}
                  className="group hover:bg-safety-50 transition-colors cursor-pointer"
                  onClick={() => setSelectedCase(c)}
                >
                  <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">{formatDateTimeJST(c.createdAt)}</td>
                  <td className="px-6 py-4">
                    <p className="font-medium text-industrial-900 text-sm">
                      {c.title}
                    </p>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-1">{c.body}</p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                      {c.teamId && <SiteChip team={teams.find((t) => t.id === c.teamId)} />}
                      {hasWhyAnalysis(c.lesson) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                          🔍 なぜなぜ分析済
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                      ${c.riskLevel === RiskLevel.HIGH ? 'bg-red-100 text-red-800' :
                        c.riskLevel === RiskLevel.MEDIUM ? 'bg-yellow-100 text-yellow-800' :
                        'bg-green-100 text-green-800'}`}>
                      {getRiskLabel(c.riskLevel)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <div className="flex flex-col space-y-1">
                      <span className="text-slate-700">{c.category1}</span>
                      <span className="text-slate-400 text-xs">#{c.category2}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getStatusColor(c.status)}`}>
                      {getStatusLabel(c.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    {c.imageUrl ? (
                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-50 text-purple-700">
                        <Camera size={12} className="mr-1" />
                        あり
                      </span>
                    ) : (
                      <span className="text-slate-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="inline-flex items-center gap-1 text-sm font-semibold text-safety-600 group-hover:text-safety-700 whitespace-nowrap">
                      詳細・対策入力
                      <span className="transition-transform group-hover:translate-x-0.5" aria-hidden>→</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredCases.length === 0 && (
          isFirstRun ? (
            <div className="text-center py-12">
              <p className="text-slate-700 font-bold mb-1">まだ報告はありません</p>
              <p className="text-sm text-slate-500 mb-4">
                QRコードを現場に掲示すると、作業員からの報告がここに届きます。
              </p>
              {onGoToQR && (
                <button
                  onClick={onGoToQR}
                  className="bg-safety-600 text-white px-5 py-2.5 rounded-lg text-sm font-bold hover:bg-safety-700 transition"
                >
                  QRコード管理へ
                </button>
              )}
            </div>
          ) : (
            <div className="text-center py-12 text-slate-500">
              <p className="mb-3">
                {noFilterActive
                  ? '該当するケースがありません。'
                  : '選択したフィルターに一致するケースがありません。'}
              </p>
              {!noFilterActive && (
                <button
                  onClick={clearFilters}
                  className="text-sm font-bold text-safety-600 hover:text-safety-700 underline"
                >
                  フィルターを解除する
                </button>
              )}
            </div>
          )
        )}
      </div>

      {/* Mobile: card view（横スクロール不要、タップで詳細モーダル） */}
      <div className="md:hidden space-y-3">
        {filteredCases.map((c) => (
          <button
            key={c.id}
            onClick={() => setSelectedCase(c)}
            className="w-full text-left bg-white rounded-xl shadow-sm border border-slate-200 p-4 hover:border-slate-300 active:bg-slate-50 transition"
          >
            <div className="flex items-center gap-2 mb-2">
              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold
                ${c.riskLevel === RiskLevel.HIGH ? 'bg-red-100 text-red-800' :
                  c.riskLevel === RiskLevel.MEDIUM ? 'bg-yellow-100 text-yellow-800' :
                  'bg-green-100 text-green-800'}`}>
                {getRiskLabel(c.riskLevel)}リスク
              </span>
              <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(c.status)}`}>
                {getStatusLabel(c.status)}
              </span>
              {c.imageUrl && (
                <Camera size={14} className="text-purple-600 ml-auto" />
              )}
            </div>
            <p className="font-medium text-industrial-900 text-sm leading-snug mb-1">
              {c.title}
            </p>
            <p className="text-xs text-slate-500 line-clamp-2 mb-2">{c.body}</p>
            <div className="flex flex-wrap items-center gap-1.5 mb-2">
              {c.teamId && <SiteChip team={teams.find((t) => t.id === c.teamId)} />}
              {hasWhyAnalysis(c.lesson) && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                  🔍 なぜなぜ分析済
                </span>
              )}
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>{c.category1} · #{c.category2}</span>
              <span className="whitespace-nowrap">{formatDateTimeJST(c.createdAt)}</span>
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-end gap-1 text-xs font-semibold text-safety-600">
              タップで詳細・対策入力
              <span aria-hidden>→</span>
            </div>
          </button>
        ))}
        {filteredCases.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 text-center py-10 px-4 text-sm">
            {isFirstRun ? (
              <>
                <p className="text-slate-700 font-bold mb-1">まだ報告はありません</p>
                <p className="text-slate-500 mb-4">
                  QRコードを現場に掲示すると、作業員からの報告がここに届きます。
                </p>
                {onGoToQR && (
                  <button
                    onClick={onGoToQR}
                    className="bg-safety-600 text-white px-5 py-2.5 rounded-lg font-bold hover:bg-safety-700 transition"
                  >
                    QRコード管理へ
                  </button>
                )}
              </>
            ) : (
              <>
                <p className="text-slate-500 mb-3">
                  {noFilterActive
                    ? '該当するケースがありません。'
                    : '選択したフィルターに一致するケースがありません。'}
                </p>
                {!noFilterActive && (
                  <button
                    onClick={clearFilters}
                    className="font-bold text-safety-600 hover:text-safety-700 underline"
                  >
                    フィルターを解除する
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Case Detail Modal */}
      {selectedCase && (
        <CaseDetailModal
          caseData={selectedCase}
          onClose={() => setSelectedCase(null)}
          onDeepAnalyze={onDeepAnalyze}
          onUpdateStatus={onUpdateCase ? (status) => {
            onUpdateCase(selectedCase.id, { status });
            setSelectedCase({ ...selectedCase, status });
          } : undefined}
        />
      )}
    </div>
  );
};

// 保存済み 4M なぜなぜ分析を構造化表示する (デモ画面と同じ amber/emerald カードUI).
// デフォルト折りたたみ + 観点タブ. パース失敗時は raw テキストにフォールバック.
const SavedWhyAnalysis: React.FC<{ parsed: ParsedWhyAnalysis; rawFallback: string }> = ({ parsed, rawFallback }) => {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  if (parsed.perspectives.length === 0) {
    return (
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
        {rawFallback}
      </div>
    );
  }

  const a = parsed.perspectives[active];
  return (
    <div className="border border-indigo-200 rounded-xl overflow-hidden">
      {/* ヘッダー (折りたたみトグル) */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 bg-indigo-50 hover:bg-indigo-100 transition text-left"
      >
        <span className="flex items-center gap-2 text-sm font-bold text-indigo-800">
          <Search size={16} />
          4M なぜなぜ分析結果
          {parsed.timestamp && <span className="text-xs font-normal text-indigo-500">({parsed.timestamp})</span>}
        </span>
        {open ? <ChevronUp size={18} className="text-indigo-600" /> : <ChevronDown size={18} className="text-indigo-600" />}
      </button>

      {open && (
        <div className="p-4 space-y-4 bg-white">
          {/* 観点タブ */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {parsed.perspectives.map((p, idx) => {
              const isActive = idx === active;
              return (
                <button
                  key={p.label}
                  onClick={() => setActive(idx)}
                  aria-pressed={isActive}
                  className={`px-2 py-2 rounded-lg text-sm font-bold border-2 transition ${
                    isActive
                      ? 'bg-safety-500 text-white border-safety-500'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-safety-300 hover:bg-safety-50'
                  }`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>

          {/* なぜなぜ 5段 */}
          <ol className="relative ml-1 pl-5 space-y-3">
            <div className="absolute left-[5px] top-2 bottom-12 w-0.5 bg-amber-200" aria-hidden />
            {a.whys.map((w, i) => (
              <li key={i} className="relative">
                <span className="absolute -left-[22px] top-2.5 w-3 h-3 rounded-full bg-amber-400 ring-4 ring-white" aria-hidden />
                <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-sm">
                  <div className="inline-block bg-amber-100 text-amber-800 text-xs font-bold px-2 py-0.5 rounded mb-1">
                    なぜ {i + 1}: {w.question}
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{w.answer}</p>
                </div>
              </li>
            ))}
            <li className="relative pt-1">
              <span className="absolute -left-[22px] top-3 w-3 h-3 rounded-full bg-emerald-500 ring-4 ring-white" aria-hidden />
              <div className="bg-white border-2 border-emerald-500 rounded-lg p-3">
                <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-bold px-2 py-0.5 rounded mb-1">推定される根本原因</div>
                <p className="text-sm font-bold text-industrial-900 leading-relaxed whitespace-pre-line">{a.rootCause}</p>
              </div>
            </li>
          </ol>

          {/* 対策カード */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <span className="bg-amber-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">即時対策</span>
              <p className="text-sm font-bold text-industrial-900 mt-1.5">{a.immediate.title}</p>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{a.immediate.description}</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
              <span className="bg-emerald-500 text-white text-[10px] font-bold px-2 py-0.5 rounded">恒久対策</span>
              <p className="text-sm font-bold text-industrial-900 mt-1.5">{a.permanent.title}</p>
              <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{a.permanent.description}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface CaseDetailModalProps {
  onDeepAnalyze?: (caseBody: string, caseId: string) => void;
  caseData: Case;
  onClose: () => void;
  onUpdateStatus?: (status: Case['status']) => void;
}

/* 管理者向けの教訓。

   非日本語の報告では教訓に「原言語版 →【日本語訳】→ 日本語版」が
   併記されている。そのまま出すと**管理者は外国語のブロックを
   読み飛ばして日本語まで下る**ことになるので、日本語を先に出し、
   原言語版は畳んでおく。

   投稿者本人のための欄でもあるので**消さない**。労災調査などで
   本人に確認する場面では、本人が読んだ文面そのものが要る。 */
const LessonForManager: React.FC<{ lesson: string }> = ({ lesson }) => {
  const { native, japanese } = splitLessonByLanguage(lesson);
  const [showNative, setShowNative] = useState(false);

  return (
    <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-sm text-amber-800">
      <div className="whitespace-pre-wrap">{japanese}</div>
      {native && (
        <div className="mt-3 pt-3 border-t border-amber-200">
          <button
            type="button"
            onClick={() => setShowNative((v) => !v)}
            className="text-xs font-medium text-amber-700 hover:text-amber-900 underline underline-offset-2"
          >
            {showNative ? '報告者の言語の版を隠す' : '報告者の言語の版を表示する'}
          </button>
          {showNative && (
            <div className="mt-2 whitespace-pre-wrap text-amber-700">{native}</div>
          )}
        </div>
      )}
    </div>
  );
};

const CaseDetailModal: React.FC<CaseDetailModalProps> = ({ caseData, onClose, onUpdateStatus, onDeepAnalyze }) => {
  const [showExpandedImage, setShowExpandedImage] = useState(false);
  const [showResolutionForm, setShowResolutionForm] = useState(false);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-industrial-900">ケース詳細</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Photo Display Section */}
          {caseData.imageUrl && (
            <div>
              <h5 className="text-sm font-semibold text-slate-600 mb-2 flex items-center">
                <Camera size={14} className="mr-1" />
                添付写真
              </h5>
              <button
                onClick={() => setShowExpandedImage(true)}
                className="relative group w-full rounded-lg overflow-hidden border border-slate-200 hover:border-slate-300 transition"
              >
                <img
                  src={caseData.imageUrl}
                  alt="報告写真"
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center">
                  <span className="opacity-0 group-hover:opacity-100 text-white text-sm font-medium bg-black/60 px-3 py-1 rounded">
                    クリックで拡大
                  </span>
                </div>
              </button>
            </div>
          )}

          <div>
            <h4 className="text-xl font-bold text-industrial-900">{caseData.title}</h4>
            <p className="text-sm text-slate-500 mt-1">{formatDateTimeJST(caseData.createdAt)}</p>
          </div>

          <div className="flex gap-2">
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
              ${caseData.riskLevel === RiskLevel.HIGH ? 'bg-red-100 text-red-800' :
                caseData.riskLevel === RiskLevel.MEDIUM ? 'bg-yellow-100 text-yellow-800' :
                'bg-green-100 text-green-800'}`}>
              {caseData.riskLevel === RiskLevel.HIGH ? '高' : caseData.riskLevel === RiskLevel.MEDIUM ? '中' : '低'}リスク
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
              {caseData.category1}
            </span>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-700">
              #{caseData.category2}
            </span>
          </div>

          {/* 4Mなぜなぜ深掘り CTA: モーダルを開いた直後に見える上部に配置 (発見性優先).
              このケースの body を preset に渡して /why-demo へ. */}
          {onDeepAnalyze && (
            <div>
              <button
                type="button"
                onClick={() => onDeepAnalyze(caseData.body || caseData.originalText, caseData.id)}
                className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-700 text-white font-bold py-3 px-4 rounded-xl shadow-md shadow-blue-500/30 hover:from-blue-700 hover:to-indigo-800 hover:-translate-y-0.5 transition"
              >
                🔍 このケースを 4M なぜなぜで深掘り分析する
              </button>
              <p className="text-xs text-slate-500 text-center mt-2">
                4観点 (人・機械・材料・方法) ごとに 5段なぜなぜ + 即時/恒久対策を AI が提示します
              </p>
            </div>
          )}

          <div>
            <h5 className="text-sm font-semibold text-slate-600 mb-2">内容</h5>
            <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap">
              {caseData.body}
            </div>
          </div>

          {/* lesson を「元の教訓」と「4Mなぜなぜ分析」に分離して表示 */}
          {(() => {
            const { base, why } = splitLessonAndWhy(caseData.lesson);
            return (
              <>
                {base && (
                  <div>
                    <h5 className="text-sm font-semibold text-slate-600 mb-2">学んだ教訓</h5>
                    <LessonForManager lesson={base} />
                  </div>
                )}
                {why && (
                  <SavedWhyAnalysis parsed={parseWhyAnalysis(why) ?? { timestamp: null, perspectives: [] }} rawFallback={why} />
                )}
              </>
            );
          })()}

          {onUpdateStatus && (
            <div>
              <h5 className="text-sm font-semibold text-slate-600 mb-2">ステータス更新</h5>
              <div className="flex flex-wrap gap-2">
                {(['open', 'in_progress'] as const).map(status => (
                  <button
                    key={status}
                    onClick={() => onUpdateStatus(status)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                      caseData.status === status
                        ? 'bg-industrial-900 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {status === 'open' ? '未対応' : '対応中'}
                  </button>
                ))}
                <button
                  onClick={() => setShowResolutionForm(true)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                    caseData.status === 'resolved'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                  }`}
                >
                  {caseData.status === 'resolved' ? '✓ 対応済み（再記録）' : '対応済みとして記録'}
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-2">
                「解決として記録」では実施した対策・効果見込みなどを残せます。投稿者本人にも見えます。
              </p>
            </div>
          )}
        </div>
      </div>

      {/* 解決記録モーダル */}
      {showResolutionForm && (
        <ResolutionModal
          caseData={caseData}
          onClose={() => setShowResolutionForm(false)}
          onSaved={() => {
            setShowResolutionForm(false);
            // 親に status 更新を伝えてローカル state を同期。
            // saveCaseResolution 内で cases.status='resolved' は更新済み。
            onUpdateStatus?.('resolved');
          }}
        />
      )}

      {/* Expanded Image Modal */}
      {showExpandedImage && caseData.imageUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90"
          onClick={() => setShowExpandedImage(false)}
        >
          <button
            onClick={() => setShowExpandedImage(false)}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white transition"
            aria-label="閉じる"
          >
            <X size={32} />
          </button>
          <img
            src={caseData.imageUrl}
            alt="報告写真（拡大）"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

// ============ Resolution Modal（B-1 改善実施記録）============

const RESOLUTION_CHECKLIST_OPTIONS: Array<{ key: ResolutionActionTag; label: string }> = [
  { key: 'procedure_review', label: '作業手順を見直した' },
  { key: 'training', label: '安全教育を実施した' },
  { key: 'equipment_fix', label: '設備に対策を施した' },
  { key: 'documentation', label: 'ルール・手順書を更新した' },
  { key: 'communication', label: '朝礼・掲示等で周知した' },
];

interface ResolutionModalProps {
  caseData: Case;
  onClose: () => void;
  onSaved: () => void;
}

const ResolutionModal: React.FC<ResolutionModalProps> = ({ caseData, onClose, onSaved }) => {
  const [actionTaken, setActionTaken] = useState('');
  const [checklist, setChecklist] = useState<Set<ResolutionActionTag>>(new Set());
  const [effectiveness, setEffectiveness] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [preventionNote, setPreventionNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 保存完了アニメーション: ボタン押下→保存OKで一旦完了画面に切替、1.4秒後に親へ通知してモーダルを閉じる。
  const [savedView, setSavedView] = useState(false);

  const toggle = (key: ResolutionActionTag) => {
    setChecklist(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSave = async () => {
    if (actionTaken.trim().length === 0) {
      setError('「実施した対策」は必須です。');
      return;
    }
    setError(null);
    setIsSaving(true);
    const result = await saveCaseResolution({
      caseId: caseData.id,
      organizationId: caseData.organizationId,
      actionTaken: actionTaken.trim(),
      actionsChecklist: Array.from(checklist),
      effectiveness,
      preventionNote: preventionNote.trim() || null,
    });
    setIsSaving(false);
    if (!result.ok) {
      setError(result.error || '保存に失敗しました');
      return;
    }
    // 完了画面を見せてから親に通知（モーダル閉じ）
    setSavedView(true);
    setTimeout(() => {
      onSaved();
    }, 1400);
  };

  // 保存完了の祝賀画面
  if (savedView) {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-10 text-center animate-fade-in">
          <div className="mx-auto mb-5 w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center ring-8 ring-emerald-50">
            <CheckCircle size={48} className="text-emerald-600" strokeWidth={2.5} />
          </div>
          <h3 className="text-xl font-bold text-industrial-900 mb-2">解決として記録しました</h3>
          <p className="text-sm text-slate-600 leading-relaxed">
            ステータスを「対応済み」に変更しました。<br />
            投稿者本人にも対応内容が表示されます。
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="bg-white rounded-2xl shadow-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-bold text-industrial-900">解決として記録</h3>
            <p className="text-xs text-slate-500 mt-0.5">投稿者本人の状況確認ページにも表示されます。</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              実施した対策 <span className="text-red-500">*</span>
            </label>
            <textarea
              value={actionTaken}
              onChange={(e) => setActionTaken(e.target.value)}
              rows={4}
              maxLength={4000}
              placeholder="例: 該当箇所にコーンを設置し、朝礼で全員に注意喚起した。"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-industrial-500 focus:border-transparent"
            />
            <p className="text-xs text-slate-400 mt-1">{actionTaken.length}/4000 文字</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              実施したアクション（任意・複数選択可）
            </label>
            <div className="space-y-1.5">
              {RESOLUTION_CHECKLIST_OPTIONS.map(opt => (
                <label key={opt.key} className="flex items-center gap-2 cursor-pointer text-sm text-slate-700 hover:text-slate-900">
                  <input
                    type="checkbox"
                    checked={checklist.has(opt.key)}
                    onChange={() => toggle(opt.key)}
                    className="rounded border-slate-300 text-industrial-600 focus:ring-industrial-500"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              効果見込み
            </label>
            <div className="flex gap-2">
              {(['Low', 'Medium', 'High'] as const).map(level => (
                <button
                  key={level}
                  onClick={() => setEffectiveness(level)}
                  className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition ${
                    effectiveness === level
                      ? level === 'High' ? 'bg-emerald-600 text-white'
                        : level === 'Medium' ? 'bg-amber-500 text-white'
                        : 'bg-slate-500 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {level === 'Low' ? '低' : level === 'Medium' ? '中' : '高'}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-400 mt-1">この対策で再発をどの程度防げそうか。</p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              再発防止策（任意）
            </label>
            <textarea
              value={preventionNote}
              onChange={(e) => setPreventionNote(e.target.value)}
              rows={3}
              maxLength={4000}
              placeholder="例: 月次パトロールのチェック項目に追加。3か月後に再評価。"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-industrial-500 focus:border-transparent"
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-6 py-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 rounded-lg disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || actionTaken.trim().length === 0}
            className="px-4 py-2 text-sm font-medium bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {isSaving && <Loader2 size={14} className="animate-spin" />}
            保存して対応済みにする
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ QR Code View ============

interface ManagerQRViewProps {
  qrPoints?: QRPoint[];
  teams?: Team[];
  organization?: { id: string; name: string; plan: 'free' | 'light' | 'standard' | 'enterprise'; qrLimit: number; qrUsed: number };
  onCreateQR?: (name: string, teamId?: string, equipmentInfo?: string) => Promise<QRPoint | null>;
  onDeleteQR?: (id: string) => void | Promise<void>;
  onToggleQR?: (id: string, isActive: boolean) => void;
  onEditQR?: (id: string, updates: { name?: string; equipmentInfo?: string; teamId?: string | null }) => Promise<boolean>;
  /** 掲示ポスターのギャラリーを、指定のQRポイントを選んだ状態で開く。 */
  onOpenPosters?: (qrPointId: string) => void;
}

export const ManagerQRView: React.FC<ManagerQRViewProps> = ({
  qrPoints: externalQRPoints,
  teams: externalTeams,
  organization: externalOrg,
  onCreateQR,
  onDeleteQR,
  onToggleQR,
  onEditQR,
  onOpenPosters
}) => {
  const storeQRPoints = useAppStore(s => s.qrPoints);
  const storeTeams = useAppStore(s => s.teams);
  const storeOrg = useAppStore(s => s.organization);

  const qrPoints = externalQRPoints || storeQRPoints;
  const teams = externalTeams || storeTeams;
  const organization = externalOrg || storeOrg;

  // Use real data only - no demo data
  const displayQRPoints = qrPoints;

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedQR, setSelectedQR] = useState<QRPoint | null>(null);
  const [editingQR, setEditingQR] = useState<QRPoint | null>(null);
  const [deletingQR, setDeletingQR] = useState<QRPoint | null>(null);

  const plan = organization?.plan || 'free';
  const planConfig = PLAN_CONFIGS[plan];
  const qrUsed = organization?.qrUsed ?? displayQRPoints.length;
  const qrLimit = organization?.qrLimit ?? planConfig.qrLimit;

  const canCreateMore = qrUsed < qrLimit;

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-industrial-900">QRコード管理</h2>
          <p className="text-sm text-slate-500 mt-1">
            使用状況: {qrUsed} / {qrLimit}（{planConfig.displayName}プラン）
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          disabled={!canCreateMore}
          className="bg-safety-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-safety-700 flex items-center shadow-sm transition active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={16} className="mr-2" />
          新規QR作成
        </button>
      </div>

      {!canCreateMore && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">QRコード数の上限に達しました</p>
            {/* 有料プランでの正しい導線は「席を追加する」。
                「アップグレード」だと、すでに契約している人が
                どこを触ればよいか分からない。 */}
            <p className="text-xs text-amber-700 mt-1">
              {planConfig.name === 'free'
                ? 'さらに作成するには「設定・プラン」からチームプランにお申し込みください。'
                : `QRコードは席1つあたり10個です。さらに必要な場合は「設定・プラン」の「メンバー」欄から席数を増やしてください。`}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {displayQRPoints.map((qr) => (
          <QRCard
            key={qr.id}
            qrPoint={qr}
            teams={teams}
            onView={() => setSelectedQR(qr)}
            onEdit={() => setEditingQR(qr)}
            onToggle={onToggleQR}
            onDelete={onDeleteQR ? () => setDeletingQR(qr) : undefined}
          />
        ))}
      </div>

      {displayQRPoints.length === 0 && (
        <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Plus className="h-8 w-8 text-slate-400" />
          </div>
          <h3 className="text-lg font-semibold text-industrial-900 mb-2">まだQRコードがありません</h3>
          <p className="text-slate-500 mb-4">最初のQRコードを作成して、レポート収集を開始しましょう。</p>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-safety-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-safety-700 transition"
          >
            QRコード作成
          </button>
        </div>
      )}

      {/* Create Modal */}
      {isCreateModalOpen && (
        <CreateQRModal
          teams={teams}
          onClose={() => setIsCreateModalOpen(false)}
          onCreate={onCreateQR}
        />
      )}

      {/* Detail/Print Modal */}
      {selectedQR && (
        <QRDetailModal
          qrPoint={selectedQR}
          onClose={() => setSelectedQR(null)}
          onOpenPosters={onOpenPosters}
        />
      )}

      {/* Edit Modal */}
      {editingQR && onEditQR && (
        <EditQRModal
          qrPoint={editingQR}
          onClose={() => setEditingQR(null)}
          onSave={onEditQR}
        />
      )}

      {/* Delete Confirm Modal */}
      {deletingQR && onDeleteQR && (
        <DeleteQRConfirmModal
          qrPoint={deletingQR}
          onClose={() => setDeletingQR(null)}
          onConfirmDelete={onDeleteQR}
          onDeactivate={
            onToggleQR
              ? (id) => {
                  onToggleQR(id, false);
                  setDeletingQR(null);
                }
              : undefined
          }
        />
      )}
    </div>
  );
};

/* 工事現場を一覧で示すチップ。

   現場に紐づけても一覧・カードのどこにも現場名が出ておらず、
   「どの現場のものか一見して分からない」状態だった。名前は長くなりがち
   （「○○ビル新築工事 3期」等）なので先頭だけ出し、全文は title 属性で見せる。 */
const SITE_LABEL_MAX = 12;

const SiteChip: React.FC<{ team?: Team | null; className?: string }> = ({ team, className = '' }) => {
  if (!team) return null;
  const short = team.name.length > SITE_LABEL_MAX
    ? `${team.name.slice(0, SITE_LABEL_MAX)}…`
    : team.name;
  return (
    <span
      title={team.name + (team.archivedAt ? '（終了）' : '')}
      className={`inline-flex items-center gap-1 max-w-full px-2 py-0.5 rounded-md text-[11px] font-medium border ${
        team.archivedAt
          ? 'bg-slate-100 text-slate-500 border-slate-200'
          : 'bg-sky-50 text-sky-700 border-sky-200'
      } ${className}`}
    >
      <MapPin size={11} className="shrink-0" />
      <span className="truncate">{short}{team.archivedAt ? '（終了）' : ''}</span>
    </span>
  );
};

interface QRCardProps {
  qrPoint: QRPoint;
  teams?: Team[];
  onView: () => void;
  onEdit?: () => void;
  onToggle?: (id: string, isActive: boolean) => void;
  onDelete?: (id: string) => void;
}

const QRCard: React.FC<QRCardProps> = ({ qrPoint, teams = [], onView, onEdit, onToggle, onDelete }) => {
  const [copied, setCopied] = useState(false);

  const qrUrl = `${window.location.origin}/report/${qrPoint.token}`;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(qrUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`bg-white rounded-xl border p-6 flex flex-col hover:shadow-md transition ${
      qrPoint.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60'
    }`}>
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-industrial-900 truncate">{qrPoint.name}</h3>
          <p className="text-xs text-slate-400 mt-1">トークン: {qrPoint.token}</p>
          {/* 現場を登録している組織では、未設定の QR も分かるようにする。
              設定漏れは一覧を見ても気づけず、集計から静かに抜け落ちる。
              現場を1つも作っていない組織には出さない（ただのノイズになる）。 */}
          {teams.some((t) => !t.archivedAt) && (
            <div className="mt-1.5">
              {qrPoint.teamId
                ? <SiteChip team={teams.find((t) => t.id === qrPoint.teamId)} />
                : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-medium border bg-slate-50 text-slate-400 border-slate-200">
                    <MapPin size={11} className="shrink-0" />現場未設定
                  </span>
                )}
            </div>
          )}
          {/* 設備情報はAI分析のコンテキストとして使われる重要項目。
              編集モーダルを開かないと確認できなかったので、一覧でも先頭だけ見えるようにする。
              全文は title 属性（ホバー）と編集モーダルで確認できる。 */}
          {qrPoint.equipmentInfo && (
            <p
              className="text-xs text-slate-500 mt-1 line-clamp-2 break-words"
              title={qrPoint.equipmentInfo}
            >
              <span className="text-slate-400">説明: </span>
              {qrPoint.equipmentInfo.length > 40
                ? `${qrPoint.equipmentInfo.slice(0, 40)}…`
                : qrPoint.equipmentInfo}
            </p>
          )}
          {!qrPoint.isActive && (
            <span className="inline-block mt-2 text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
              無効
            </span>
          )}
        </div>
        <div className="bg-white p-1 border border-slate-100 rounded shadow-inner ml-4">
          <QRCodeSVG value={qrUrl} size={48} level="M" />
        </div>
      </div>

      <div className="space-y-2 text-sm">
        <div className="flex justify-between">
          <span className="text-slate-500">スキャン数</span>
          <span className="font-medium">{qrPoint.scans}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">レポート数</span>
          <span className="font-medium">{qrPoint.posts}</span>
        </div>
        <div className="flex justify-between">
          <span className="text-slate-500">最終スキャン</span>
          <span className="font-medium text-xs">{formatDateTimeJST(qrPoint.lastScan)}</span>
        </div>
      </div>

      <div className="mt-auto pt-4 border-t border-slate-100 space-y-3">
        <div className="flex space-x-2">
          <button
            onClick={onView}
            className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-600 py-2 rounded-lg text-xs font-medium flex items-center justify-center transition"
          >
            <Printer size={14} className="mr-1" /> 印刷
          </button>
          <button
            onClick={handleCopy}
            className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-600 py-2 rounded-lg text-xs font-medium flex items-center justify-center transition"
          >
            {copied ? (
              <>
                <CheckCircle size={14} className="mr-1 text-green-600" /> コピー済
              </>
            ) : (
              <>
                <Copy size={14} className="mr-1" /> URL
              </>
            )}
          </button>
          {onEdit && (
            <button
              onClick={onEdit}
              className="flex-1 bg-slate-50 hover:bg-slate-100 text-slate-600 py-2 rounded-lg text-xs font-medium flex items-center justify-center transition"
            >
              <Edit3 size={14} className="mr-1" /> 編集
            </button>
          )}
        </div>

        <div className="flex items-center justify-between">
          {onToggle && (
            <button
              onClick={() => onToggle(qrPoint.id, !qrPoint.isActive)}
              className="text-xs text-slate-500 hover:text-slate-700 flex items-center"
            >
              {qrPoint.isActive ? (
                <><ToggleRight size={18} className="mr-1 text-green-500" /> 有効</>
              ) : (
                <><ToggleLeft size={18} className="mr-1" /> 無効</>
              )}
            </button>
          )}
          {onDelete && (
            <button
              onClick={() => onDelete(qrPoint.id)}
              className="text-xs text-red-500 hover:text-red-700 flex items-center"
            >
              <Trash2 size={14} className="mr-1" /> 削除
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

interface DeleteQRConfirmModalProps {
  qrPoint: QRPoint;
  onClose: () => void;
  onConfirmDelete: (id: string) => void | Promise<void>;
  /** 「削除ではなく無効化」への逃げ道。onToggleQR が無い文脈では出さない。 */
  onDeactivate?: (id: string) => void;
}

/**
 * QR削除の確認モーダル。
 * 削除されたQRは現場に掲示済みのポスターに印刷されており、紙は回収できない。
 * 誤タップ1回で全ポスターが死ぬため、(1)掲示済みの証拠（スキャン実績）を見せ、
 * (2)可逆な「無効化」へ誘導し、(3)削除は赤ボタンに分離する。
 */
const DeleteQRConfirmModal: React.FC<DeleteQRConfirmModalProps> = ({
  qrPoint,
  onClose,
  onConfirmDelete,
  onDeactivate,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  // Esc で閉じる（削除実行中は閉じない）
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isDeleting) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDeleting, onClose]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await onConfirmDelete(qrPoint.id);
      onClose();
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      onClick={() => { if (!isDeleting) onClose(); }}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-qr-title"
      >
        <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 id="delete-qr-title" className="text-lg font-bold text-industrial-900">
            QRコードを削除しますか？
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            対象: <span className="font-bold text-industrial-900">{qrPoint.name}</span>
          </p>

          <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-1">
            <div className="flex justify-between">
              <span className="text-slate-500">スキャン数</span>
              <span className="font-medium">{qrPoint.scans} 回</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">レポート数</span>
              <span className="font-medium">{qrPoint.posts} 件</span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-500">最終スキャン</span>
              <span className="font-medium text-xs">{formatDateTimeJST(qrPoint.lastScan)}</span>
            </div>
          </div>

          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-red-800 leading-relaxed">
              <p className="font-bold mb-1">削除すると元に戻せません</p>
              <p>
                印刷して現場に掲示済みのポスターのQRコードも読み取れなくなり、
                作業員には「無効なQRコード」画面が表示されます。
              </p>
            </div>
          </div>

          {qrPoint.isActive && onDeactivate && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800 mb-3">
                報告の受付を一時的に止めたいだけなら「無効化」がおすすめです。
                無効化はいつでも元に戻せます。
              </p>
              <button
                type="button"
                onClick={() => onDeactivate(qrPoint.id)}
                className="w-full px-4 py-3 bg-white border border-amber-300 text-amber-800 rounded-lg font-bold hover:bg-amber-100 transition flex items-center justify-center"
              >
                <ToggleLeft size={18} className="mr-2" />
                削除せず無効化する（推奨）
              </button>
            </div>
          )}
        </div>

        <div className="px-6 pb-6 flex space-x-3">
          <button
            type="button"
            onClick={onClose}
            disabled={isDeleting}
            className="flex-1 px-4 py-3 border border-slate-200 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
          >
            キャンセル
          </button>
          <button
            type="button"
            onClick={handleDelete}
            disabled={isDeleting}
            className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-bold hover:bg-red-700 transition disabled:opacity-50 flex items-center justify-center"
          >
            {isDeleting ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                削除中...
              </>
            ) : (
              <>
                <Trash2 size={16} className="mr-2" />
                削除する
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

interface CreateQRModalProps {
  teams: Team[];
  onClose: () => void;
  onCreate?: (name: string, teamId?: string, equipmentInfo?: string) => Promise<QRPoint | null>;
}

const CreateQRModal: React.FC<CreateQRModalProps> = ({ teams, onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [teamId, setTeamId] = useState('');
  const [equipmentInfo, setEquipmentInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('QRコードの名前を入力してください');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (onCreate) {
        logger.log('Creating QR with name:', name.trim(), 'teamId:', teamId, 'equipmentInfo:', equipmentInfo);
        const result = await onCreate(name.trim(), teamId || undefined, equipmentInfo || undefined);
        logger.log('QR creation result:', result);
        if (!result) throw new Error('QRコードの作成に失敗しました。しばらくしてからお試しください。');
      }
      onClose();
    } catch (err) {
      logger.error('QR creation error in UI:', err);
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-industrial-900">新規QRコード作成</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              QRコード名 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 工場ラインA"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition"
              required
            />
          </div>

          {/* 終了した工事現場は新しい QR の割り当て先に出さない。
              終わった現場に貼る掲示物は無いため。 */}
          {teams.filter((t) => !t.archivedAt).length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                工事現場（任意）
              </label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition"
              >
                <option value="">現場を指定しない</option>
                {teams.filter((t) => !t.archivedAt).map(team => (
                  <option key={team.id} value={team.id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              設備情報（オプション）
            </label>
            <textarea
              value={equipmentInfo}
              onChange={(e) => setEquipmentInfo(e.target.value)}
              placeholder="例: ラインA組立機・稼働15年・月産3000個&#10;※入力するとAIが設備特性を考慮した改善提案を行います"
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">
              設備の種類、稼働年数、生産能力などを入力すると、より具体的な改善提案が可能になります
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-safety-600 text-white rounded-lg font-medium hover:bg-safety-700 transition disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  作成中...
                </>
              ) : (
                'QRコード作成'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface EditQRModalProps {
  qrPoint: QRPoint;
  onClose: () => void;
  onSave: (id: string, updates: { name?: string; equipmentInfo?: string; teamId?: string | null }) => Promise<boolean>;
}

const EditQRModal: React.FC<EditQRModalProps> = ({ qrPoint, onClose, onSave }) => {
  const [name, setName] = useState(qrPoint.name);
  const [equipmentInfo, setEquipmentInfo] = useState(qrPoint.equipmentInfo || '');
  /* 既存のQRに後から工事現場を割り当てられるようにする。
     掲示済みのポスターを作り直すとトークンが変わって再印刷になるため、
     作成時にしか設定できないと運用中の顧客は現場機能を使えない。 */
  const teams = useAppStore((st) => st.teams);
  const [teamId, setTeamId] = useState(qrPoint.teamId || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('QRコードの名前を入力してください');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const success = await onSave(qrPoint.id, {
        name: name.trim(),
        equipmentInfo: equipmentInfo.trim() || undefined
      });
      if (!success) {
        throw new Error('QRコードの更新に失敗しました。しばらくしてからお試しください。');
      }
      /* 現場が変わったときだけ更新する。**これまでの報告は動かさない**
         （報告は「いつ・どこで起きたか」の記録であり、掲示物の
         付け替えで過去の事実を書き換えない）。 */
      if ((qrPoint.teamId || '') !== teamId) {
        const ok = await setQRPointTeam(qrPoint.id, teamId || null);
        if (!ok) {
          throw new Error('工事現場を変更できませんでした。しばらくしてからお試しください。');
        }
        /* 画面の一覧が古いままにならないよう、親にも伝える。
           伝えないと再編集したときに現場が元の値に見え、
           差分判定が狂って「変えたのに変わらない」ことになる。 */
        await onSave(qrPoint.id, { teamId: teamId || null });
      }
      onClose();
    } catch (err) {
      logger.error('QR update error:', err);
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
        <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-industrial-900">QRコード編集</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              QRコード名 *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="例: 工場ラインA"
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition"
              required
            />
          </div>

          {teams.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                工事現場（任意）
              </label>
              <select
                value={teamId}
                onChange={(e) => setTeamId(e.target.value)}
                className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition"
              >
                <option value="">現場を指定しない</option>
                {teams.filter((t) => !t.archivedAt || t.id === qrPoint.teamId).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}{t.archivedAt ? '（終了）' : ''}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-slate-500">
                現場を変えても、これまでの報告は元の現場の記録として残ります。
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              設備情報（オプション）
            </label>
            <textarea
              value={equipmentInfo}
              onChange={(e) => setEquipmentInfo(e.target.value)}
              placeholder="例: ラインA組立機・稼働15年・月産3000個&#10;※入力するとAIが設備特性を考慮した改善提案を行います"
              rows={3}
              className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-transparent transition resize-none"
            />
            <p className="text-xs text-slate-400 mt-1">
              設備の種類、稼働年数、生産能力などを入力すると、より具体的な改善提案が可能になります
            </p>
          </div>

          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg flex items-center space-x-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 border border-slate-200 rounded-lg font-medium text-slate-700 hover:bg-slate-50 transition"
            >
              キャンセル
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-safety-600 text-white rounded-lg font-medium hover:bg-safety-700 transition disabled:opacity-50 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  保存中...
                </>
              ) : (
                '変更を保存'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface QRDetailModalProps {
  qrPoint: QRPoint;
  onClose: () => void;
  /** 掲示ポスターのギャラリーを、このQRポイントを選んだ状態で開く。 */
  onOpenPosters?: (qrPointId: string) => void;
}

const QRDetailModal: React.FC<QRDetailModalProps> = ({ qrPoint, onClose, onOpenPosters }) => {
  const canvasRef = useRef<HTMLDivElement>(null);
  const qrUrl = `${window.location.origin}/report/${qrPoint.token}`;

  const handleDownload = useCallback(() => {
    const canvas = canvasRef.current?.querySelector('canvas');
    if (!canvas) return;

    const link = document.createElement('a');
    link.download = `qr-${qrPoint.token}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  }, [qrPoint.token]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full">
        <div className="border-b border-slate-100 px-6 py-4 flex justify-between items-center">
          <h3 className="text-lg font-bold text-industrial-900">{qrPoint.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" aria-label="閉じる">
            <X size={24} />
          </button>
        </div>

        <div className="p-6">
          {/* QR Code Preview */}
          <div className="bg-white border-4 border-industrial-900 p-6 text-center mb-6">
            <h2 className="text-2xl font-extrabold text-industrial-900 mb-1">安全レポート</h2>
            <p className="text-lg font-bold text-safety-600 mb-6">スキャンして報告！</p>
            <div ref={canvasRef} className="flex justify-center mb-6">
              <QRCodeCanvas value={qrUrl} size={200} level="H" />
            </div>
            <p className="text-sm text-slate-500">{qrPoint.name}</p>
          </div>

          {/* URL */}
          <div className="bg-slate-50 rounded-lg p-3 mb-6">
            <p className="text-xs text-slate-500 mb-1">QRコード URL:</p>
            <p className="text-sm text-slate-700 break-all font-mono">{qrUrl}</p>
          </div>

          {/* Actions */}
          <div className="flex space-x-3">
            <button
              onClick={handleDownload}
              className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 py-3 rounded-lg font-medium flex items-center justify-center transition"
            >
              <Download size={18} className="mr-2" /> PNGダウンロード
            </button>
            {/* 掲示ポスターは /posters のギャラリーに一本化した。
                旧実装（QRだけのA4）は緊急連絡先の記入欄も根拠法令も無く、
                LPで訴求している掲示物と別物だったので削除している。 */}
            <button
              onClick={() => onOpenPosters?.(qrPoint.id)}
              disabled={!onOpenPosters}
              className="flex-1 bg-industrial-900 hover:bg-slate-800 text-white py-3 rounded-lg font-medium flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Printer size={18} className="mr-2" /> ポスターを作る
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============ Settings View ============

/**
 * メンバー管理。
 *
 * 席数の上限は Edge Function が強制する。ここでは「押せるが断られる」形にして、
 * 断られた理由（有料プランが必要 / 席が足りない）をそのまま画面に出す。
 * ボタンを消してしまうと、なぜ追加できないのかが伝わらない。
 */
const MembersSection: React.FC = () => {
  const { organization, user, setOrganization } = useAppStore();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [invites, setInvites] = useState<OrgInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'admin' | 'leader'>('leader');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [newSeats, setNewSeats] = useState(organization?.seats ?? 1);
  const [seatBusy, setSeatBusy] = useState(false);
  /* 席数のエラーは席数の操作の近くに出す。メンバー追加と同じ state を
     使っていたため、席数変更を断られた文言が「メンバーを追加」欄の下に
     出ていた。原因の操作から離れた場所に出ると、何が起きたか分からない。 */
  const [seatError, setSeatError] = useState<string | null>(null);
  const [seatNotice, setSeatNotice] = useState<string | null>(null);

  const isAdmin = user?.role === 'admin';
  const seats = organization?.seats ?? 1;
  const used = members.length + invites.filter((i) => i.status === 'pending').length;

  const reload = useCallback(async () => {
    if (!organization?.id) return;
    setLoading(true);
    const [m, i] = await Promise.all([
      getOrgMembers(organization.id),
      getPendingInvites(organization.id),
    ]);
    setMembers(m);
    setInvites(i);
    setLoading(false);
  }, [organization?.id]);

  useEffect(() => { void reload(); }, [reload]);

  /* 組織が後から読み込まれる場合に席数の初期値が 1 のまま固まらないようにする。 */
  useEffect(() => {
    if (organization?.seats) setNewSeats(organization.seats);
  }, [organization?.seats]);

  const handleSeatChange = async () => {
    if (!organization?.id) return;
    setSeatError(null); setSeatNotice(null); setSeatBusy(true);
    const res = await updateSeats(newSeats);
    if (!res.success) { setSeatBusy(false); setSeatError(res.message); return; }
    /* DB への反映は Stripe → webhook 経由なので即時ではない。
       固定時間だけ待って1回読むと、間に合わなかったときに**古い席数を
       書き戻して**しまい、「変更しました」の文言と数字が食い違う。
       反映を確認できるまで数回試し、確認できなければ正直にそう伝える。 */
    let applied = false;
    for (let i = 0; i < 4; i++) {
      await new Promise((r) => setTimeout(r, 2000));
      const fresh = await getOrganization(organization.id);
      if (fresh) {
        setOrganization(fresh);
        if (fresh.seats === newSeats) { applied = true; break; }
      }
    }
    setSeatBusy(false);
    setSeatNotice(
      applied
        ? `席数を ${newSeats} 人に変更しました。`
        : `お支払い側の変更は完了しました。画面への反映に時間がかかっています。`
          + `しばらくしてから画面を再読み込みしてください。`,
    );
    await reload();
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setNotice(null); setBusy(true);
    const res = await inviteMember(email, role);
    setBusy(false);
    if (!res.success) { setError(res.message); return; }
    setEmail('');
    setNotice('登録しました。ご本人に「anzenpost.com にこのメールアドレスでログイン」とお伝えください。');
    await reload();
  };

  if (!organization) return null;

  /* leader は RLS で他のメンバーを読めない（"Admins can view org users"）。
     そのまま出すと「1/5席」のような**嘘の数字**になるので、
     管理者以外には数字を出さず、誰に頼めばよいかだけ伝える。 */
  if (!isAdmin) {
    return (
      <div>
        <h2 className="text-2xl font-bold text-industrial-900 mb-2">メンバー</h2>
        <div className="bg-white rounded-xl border border-slate-200 p-5 text-sm text-slate-600">
          メンバーの追加・削除は管理者が行います。
        </div>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl font-bold text-industrial-900 mb-2">メンバー</h2>
      <p className="text-sm text-slate-600 mb-6">
        管理画面を使う方を登録します。
        <strong className="text-industrial-900">報告する作業員はアカウント不要・人数無制限</strong>です。
      </p>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        {/* 席の使用状況 */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm text-slate-600">
            <span className="font-bold text-industrial-900 text-lg">{used}</span>
            <span className="mx-1">/</span>
            <span>{seats} 席</span>
            {used > seats && (
              <span className="ml-2 text-red-600 font-semibold">席数を超えています</span>
            )}
          </div>
          <div className="text-xs text-slate-500">
            1席目 ¥{PLAN_CONFIGS.light.price.toLocaleString()} ／
            2席目以降 1席 ¥{SEAT_PRICE.toLocaleString()}
          </div>
        </div>

        {used > seats && (
          <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              登録人数が席数を超えています。現在のメンバーはそのままご利用いただけますが、
              新しい方の追加はできません。席を追加するか、メンバーを減らしてください。
            </span>
          </div>
        )}

        {/* 一覧 */}
        {loading ? (
          <p className="text-sm text-slate-500">読み込み中...</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {members.map((m) => (
              <li key={m.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-industrial-900 truncate">{m.name}</p>
                  <p className="text-xs text-slate-500 truncate">{m.email}</p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    m.role === 'admin' ? 'bg-safety-50 text-safety-700' : 'bg-slate-100 text-slate-600'
                  }`}>
                    {m.role === 'admin' ? '管理者' : '担当者'}
                  </span>
                  {isAdmin && m.id !== user?.id && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`${m.name} さんを外しますか？`)) return;
                        const res = await removeMember(m.id);
                        if (!res.success) { setError(res.message); return; }
                        await reload();
                      }}
                      className="text-xs text-slate-400 hover:text-red-600 px-2 py-1"
                    >
                      外す
                    </button>
                  )}
                </div>
              </li>
            ))}
            {invites.map((i) => (
              <li key={i.id} className="py-3 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm text-slate-700 truncate">{i.email}</p>
                  <p className="text-xs text-slate-400">
                    {i.status === 'seat_blocked' ? '席数が足りず保留中' : 'ログイン待ち'}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={`text-xs px-2 py-1 rounded-full font-semibold ${
                    i.status === 'seat_blocked' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'
                  }`}>
                    {i.status === 'seat_blocked' ? '保留' : '招待済み'}
                  </span>
                  {isAdmin && (
                    <button
                      onClick={async () => {
                        const res = await revokeInvite(i.id);
                        if (!res.success) { setError(res.message); return; }
                        await reload();
                      }}
                      className="text-xs text-slate-400 hover:text-red-600 px-2 py-1"
                    >
                      取消
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* 席数の変更。**契約者本人のみ**サーバーが許可する。
            他の管理者が押すと 403 が返るので、その文言をそのまま出す。
            ボタンを隠すより「なぜできないか」が伝わる。 */}
        {isAdmin && organization.plan !== 'free' && (
          <div className="pt-2 border-t border-slate-100 space-y-3">
            <p className="text-sm font-semibold text-industrial-900">席数を変更</p>
            {/* 使っていない席はそのまま請求される。気づけるようにする。 */}
            {seats > used && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                {seats - used}席 あいています。使わない席も請求に含まれるため、
                減らす場合は人数を選んで「席数を変更」を押してください。
              </p>
            )}
            <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
              <select
                value={newSeats}
                onChange={(e) => setNewSeats(Number(e.target.value))}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                {Array.from({ length: MAX_SELF_SERVE_SEATS }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>{n}人</option>
                ))}
              </select>
              <span className="text-sm text-slate-600">
                月額 ¥{priceForSeats(newSeats).toLocaleString()}
              </span>
              <button
                onClick={handleSeatChange}
                disabled={seatBusy || newSeats === seats}
                className="px-4 py-2 bg-industrial-900 hover:bg-industrial-800 disabled:opacity-40 text-white text-sm font-bold rounded-lg"
              >
                {seatBusy ? '変更中...' : '席数を変更'}
              </button>
              {/* 同数のときボタンが無効になる。理由を書かないと壊れて見える。 */}
              {newSeats === seats && !seatBusy && (
                <span className="text-xs text-slate-500">
                  いまは {seats}人 です。人数を選ぶと変更できます
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500">
              増やした分は日割りで次回の請求に加算されます。
              減らした場合は返金ではなく、次回以降の請求から差し引かれます。
              変更できるのはご契約者ご本人のみです。
            </p>
            {seatError && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{seatError}</span>
              </div>
            )}
            {/* 成功の知らせも席数欄の直下に出す。エラー側だけ直したため、
                「席数を変更しました」がメンバー追加欄の下に出ていた。 */}
            {seatNotice && (
              <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <span>{seatNotice}</span>
              </div>
            )}
          </div>
        )}

        {/* 追加 */}
        {isAdmin && (
          <form onSubmit={handleInvite} className="pt-2 border-t border-slate-100 space-y-3">
            <p className="text-sm font-semibold text-industrial-900">メンバーを追加</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="tanaka@example.co.jp"
                required
                className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-safety-500 focus:outline-none"
              />
              <select
                value={role}
                onChange={(e) => setRole(e.target.value as 'admin' | 'leader')}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm"
              >
                <option value="leader">担当者</option>
                <option value="admin">管理者</option>
              </select>
              <button
                type="submit"
                disabled={busy || !email.trim()}
                className="px-4 py-2 bg-safety-500 hover:bg-safety-600 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
              >
                {busy ? '登録中...' : '追加'}
              </button>
            </div>
            <p className="text-xs text-slate-500">
              追加した方は、ご自身で anzenpost.com にログインするとこの組織に参加します。
              管理者は席数の変更とメンバーの追加ができます。
            </p>
            {error && (
              <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
            {notice && (
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                {notice}
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

/**
 * 工事現場。
 *
 * **コードでは team、画面では「工事現場」**（テーブル名は据え置き）。
 * 終了はアーカイブが主で、削除は出さない——報告が現場に紐づいているため、
 * 消す操作を目立つ場所に置かない。
 */
const WorkSitesSection: React.FC = () => {
  const { organization } = useAppStore();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  const reload = useCallback(async () => {
    if (!organization?.id) return;
    setLoading(true);
    setTeams(await getTeams(organization.id));
    setLoading(false);
  }, [organization?.id]);

  useEffect(() => { void reload(); }, [reload]);

  const active = teams.filter((t) => !t.archivedAt);
  const archived = teams.filter((t) => t.archivedAt);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organization?.id || !name.trim()) return;
    setBusy(true); setError(null);
    const created = await createTeam(organization.id, name.trim());
    setBusy(false);
    if (!created) { setError('工事現場を登録できませんでした。'); return; }
    setName('');
    await reload();
  };

  if (!organization) return null;

  return (
    <div>
      <h2 className="text-2xl font-bold text-industrial-900 mb-2">工事現場</h2>
      <p className="text-sm text-slate-600 mb-6">
        現場ごとに報告をまとめられます。QRコードを現場に紐づけると、
        その現場の報告として記録されます。
      </p>

      <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-5">
        {loading ? (
          <p className="text-sm text-slate-500">読み込み中...</p>
        ) : active.length === 0 ? (
          <p className="text-sm text-slate-500">
            まだ工事現場がありません。登録すると、ダッシュボードとケース一覧を現場ごとに絞り込めます。
          </p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {active.map((t) => (
              <li key={t.id} className="py-3 flex items-center justify-between gap-3">
                <span className="font-semibold text-industrial-900 truncate">{t.name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={async () => {
                      const next = window.prompt('工事現場の名前', t.name);
                      if (!next || next.trim() === t.name) return;
                      if (!(await renameTeam(t.id, next))) { setError('名前を変更できませんでした。'); return; }
                      await reload();
                    }}
                    className="text-xs text-slate-500 hover:text-industrial-900 px-2 py-1"
                  >
                    名前を変更
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(
                        `「${t.name}」を終了しますか？\n\n` +
                        'これまでの報告は残ります。終了した現場は新しいQRの割り当て先から外れます。',
                      )) return;
                      if (!(await archiveTeam(t.id))) { setError('終了できませんでした。'); return; }
                      await reload();
                    }}
                    className="text-xs text-slate-500 hover:text-amber-700 px-2 py-1"
                  >
                    終了する
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        <form onSubmit={handleCreate} className="pt-2 border-t border-slate-100 flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="○○ビル新築工事"
            className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm focus:border-safety-500 focus:outline-none"
          />
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="px-4 py-2 bg-safety-500 hover:bg-safety-600 disabled:opacity-50 text-white text-sm font-bold rounded-lg"
          >
            {busy ? '登録中...' : '現場を追加'}
          </button>
        </form>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {archived.length > 0 && (
          <div className="pt-2 border-t border-slate-100">
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-xs text-slate-500 hover:text-industrial-900"
            >
              終了した現場（{archived.length}）{showArchived ? 'を隠す' : 'を表示'}
            </button>
            {showArchived && (
              <ul className="mt-2 divide-y divide-slate-100">
                {archived.map((t) => (
                  <li key={t.id} className="py-2 flex items-center justify-between gap-3">
                    <span className="text-sm text-slate-500 truncate">{t.name}</span>
                    <button
                      onClick={async () => {
                        if (!(await unarchiveTeam(t.id))) { setError('再開できませんでした。'); return; }
                        await reload();
                      }}
                      className="text-xs text-slate-500 hover:text-industrial-900 px-2 py-1"
                    >
                      再開する
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export const ManagerSettingsView: React.FC = () => {
  const { organization, setOrganization } = useAppStore();
  const [currentPlan, setCurrentPlan] = useState(organization?.plan || 'free');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);
  /* 申し込む席数。既定は現在の席数（未契約なら1）。
     **契約中はこの値を使わない**——契約中のカードは実際の席数を出す。 */
  const [seatCount, setSeatCount] = useState(organization?.seats ?? 1);
  const [orgName, setOrgName] = useState(organization?.name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);

  /* カードに出す席数。未契約なら「これから申し込む数」、
     契約中なら「いま契約している数」。 */
  const shownSeats = currentPlan === 'free' ? seatCount : (organization?.seats ?? 1);

  /* 決済から戻ってきたときの状態。
     'waiting' = 反映を待っている / 'done' = 反映された /
     'slow'    = 待っても反映されない / 'canceled' = 途中でやめた */
  const [checkoutReturn, setCheckoutReturn] =
    useState<'none' | 'waiting' | 'done' | 'slow' | 'canceled'>('none');

  /* Stripe から戻ってきたときの処理。

     これが無かったため、決済が終わっても画面はフリー・1席のままで、
     申し込みボタンも押せる状態だった。付与は webhook 経由で数秒遅れる。
     利用者は「決済が失敗した」と思ってもう一度申し込む——結果として
     サブスクリプションが2本でき、二重に請求される。
     （2本目の作成自体は stripe-checkout 側で止めたが、
     **何が起きているか分からない状態を残すべきではない。**）

     **クエリの読み取りと反映待ちは分ける。** 画面が立ち上がる時点では
     organization がまだ入っていないことがあり、同じ effect で
     「クエリを消して organization が無いから抜ける」と、次に
     organization が入ったときには読む対象が消えている（実際にそうなり、
     バナーが一度も出なかった）。 */
  /* 解約手続き済みのとき、いつまで使えるかを出すための情報。
     Stripe の解約は「期間末で終了」なので、手続き後もしばらく
     チームプランのままになる。日付を出さないと
     「解約したのにチームと出ている」と不安になる。 */
  const [endsOn, setEndsOn] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      const authUser = await getCurrentUser();
      if (!alive || !authUser) return;
      const sub = await getUserSubscription(authUser.id);
      if (!alive) return;
      setEndsOn(sub?.cancelAtPeriodEnd && sub.currentPeriodEnd ? sub.currentPeriodEnd : null);
    })();
    return () => { alive = false; };
    /* プランが変わったら読み直す（解約直後・再契約直後）。 */
  }, [currentPlan, organization?.seats]);

  /* 戻ってきた種別は起動時（index.tsx の captureCheckoutReturn）に
     控えてある。ここで読むだけにする——App の初期化が pathname だけで
     replaceState するため、この画面が描かれる時点でクエリは消えている。 */
  const [returnedFrom] = useState<'success' | 'canceled' | null>(() => readCheckoutReturn());

  useEffect(() => {
    if (!returnedFrom) return;
    setCheckoutReturn(returnedFrom === 'canceled' ? 'canceled' : 'waiting');
  }, [returnedFrom]);

  /* 表示を閉じたら二度目は出さない。 */
  const dismissCheckoutReturn = useCallback(() => {
    clearCheckoutReturn();
    setCheckoutReturn('none');
  }, []);

  useEffect(() => {
    if (returnedFrom !== 'success' || !organization?.id) return;
    let alive = true;
    (async () => {
      /* 2秒 × 8回。webhook が Stripe → Supabase と経由するため、
         1〜2回で入らないことがある。 */
      for (let i = 0; i < 8 && alive; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const fresh = await getOrganization(organization.id);
        if (!alive) return;
        if (fresh) {
          setOrganization(fresh);
          setCurrentPlan(fresh.plan);
          setSeatCount(fresh.seats ?? 1);
          if (fresh.plan !== 'free') {
            setCheckoutReturn('done');
            clearCheckoutReturn();
            return;
          }
        }
      }
      if (alive) setCheckoutReturn('slow');
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnedFrom, organization?.id]);

  // Fetch organization from DB
  useEffect(() => {
    const fetchData = async () => {
      if (!organization?.id) return;

      try {
        // Refresh organization data from DB
        const orgData = await getOrganization(organization.id);
        if (orgData) {
          setOrganization(orgData);
          setCurrentPlan(orgData.plan);
          setOrgName(orgData.name);
          /* 席数も同期する。しないと「現在のプラン」と書いたカードが
             古い席数（初期値の1人など）の金額・上限を出し続ける。 */
          setSeatCount(orgData.seats ?? 1);
        }
      } catch (error) {
        logger.error('Failed to fetch organization:', error);
      }
    };

    fetchData();
  }, [organization?.id, setOrganization]);

  const handleSaveOrgName = async () => {
    if (!organization?.id || !orgName.trim()) return;

    setIsSavingName(true);
    try {
      const success = await updateOrganizationName(organization.id, orgName.trim());
      if (success) {
        setOrganization({ ...organization, name: orgName.trim() });
        setIsEditingName(false);
      } else {
        alert('組織名の更新に失敗しました');
      }
    } catch (error) {
      logger.error('Failed to update organization name:', error);
      alert('組織名の更新に失敗しました');
    } finally {
      setIsSavingName(false);
    }
  };

  const handlePlanChange = async (planName: 'free' | 'light', seats = 1) => {
    if (!organization?.id) {
      alert('組織情報が取得できません');
      return;
    }

    // フリープランへの変更はStripe顧客ポータルでキャンセル
    if (planName === 'free') {
      if (isStripeConfigured()) {
        openCustomerPortal(`${window.location.origin}/manager/settings`);
      } else {
        alert('サブスクリプションのキャンセルはStripe顧客ポータルから行ってください');
      }
      return;
    }

    // 有料プランへの変更
    if (!isStripeConfigured()) {
      alert('Stripeが設定されていません。決済機能は現在利用できません。');
      return;
    }

    setLoadingPlan(planName);

    try {
      const result = await createCheckoutSession(
        planName,
        organization.id,
        `${window.location.origin}/manager/settings?success=true`,
        `${window.location.origin}/manager/settings?canceled=true`,
        seats
      );

      if (result?.error) {
        alert(`決済ページの作成に失敗しました:\n${result.error}`);
      } else if (result?.url) {
        window.location.href = result.url;
      } else {
        alert('決済ページの作成に失敗しました。しばらくしてからお試しください。');
      }
    } catch (error) {
      logger.error('Checkout error:', error);
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      alert(`エラーが発生しました:\n${errorMessage}`);
    } finally {
      setLoadingPlan(null);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Organization Settings */}
      <div>
        <h2 className="text-2xl font-bold text-industrial-900 mb-6">組織設定</h2>

        {/* 決済から戻ってきたときの状態。何が起きているかを必ず伝える。
            黙っていると「失敗した」と思って二度目の申し込みに向かう。 */}
        {checkoutReturn === 'waiting' && (
          <div className="mb-6 rounded-xl border-2 border-sky-300 bg-sky-50 p-4 flex items-start gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-sky-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-industrial-900">お手続きを確認しています</p>
              <p className="mt-1 text-sm text-slate-700">
                お支払いは完了しています。プランの反映まで数秒かかります。この画面のままお待ちください。
              </p>
            </div>
          </div>
        )}
        {checkoutReturn === 'done' && (
          <div className="mb-6 rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
            <p className="text-sm font-bold text-industrial-900">お申し込みが完了しました</p>
            <p className="mt-1 text-sm text-slate-700">
              下の「メンバー」欄から、一緒に使う方を追加できます。
              追加した方は、ご自身で anzenpost.com にログインするとこの組織に参加します。
            </p>
            <button
              onClick={dismissCheckoutReturn}
              className="mt-3 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-medium rounded-lg"
            >
              閉じる
            </button>
          </div>
        )}
        {checkoutReturn === 'slow' && (
          <div className="mb-6 rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-bold text-industrial-900">反映に時間がかかっています</p>
            <p className="mt-1 text-sm text-slate-700">
              <strong>お支払いは完了しています。</strong>プランの反映だけが遅れています。
              1〜2分ほどしてからこの画面を再読み込みしてください。
            </p>
            <p className="mt-2 text-sm text-slate-700">
              <strong>もう一度お申し込みはしないでください。</strong>
              二重にご請求が発生する可能性があります。解消しない場合は
              support@genbacompass.com までご連絡ください。
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 px-4 py-2 bg-industrial-900 hover:bg-industrial-800 text-white text-sm font-bold rounded-lg"
            >
              再読み込みする
            </button>
          </div>
        )}
        {checkoutReturn === 'canceled' && (
          <div className="mb-6 rounded-xl border border-slate-300 bg-slate-50 p-4">
            <p className="text-sm text-slate-700">
              お申し込みを中止しました。ご請求は発生していません。
            </p>
            <button
              onClick={dismissCheckoutReturn}
              className="mt-2 px-3 py-1.5 bg-white hover:bg-slate-50 border border-slate-300 text-slate-700 text-xs font-medium rounded-lg"
            >
              閉じる
            </button>
          </div>
        )}
        <div className="bg-white rounded-xl border border-slate-200 p-6 max-w-md">
          <div className="flex items-center mb-4">
            <Building2 className="h-5 w-5 text-slate-500 mr-2" />
            <span className="text-sm font-medium text-slate-600">組織名</span>
          </div>

          {isEditingName ? (
            <div className="space-y-3">
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-safety-500 focus:border-safety-500 outline-none transition"
                placeholder="組織名を入力"
              />
              <div className="flex space-x-2">
                <button
                  onClick={handleSaveOrgName}
                  disabled={isSavingName || !orgName.trim()}
                  className="flex-1 bg-industrial-900 hover:bg-slate-800 text-white py-2 px-4 rounded-lg font-medium flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSavingName ? (
                    <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      保存
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setIsEditingName(false);
                    setOrgName(organization?.name || '');
                  }}
                  disabled={isSavingName}
                  className="px-4 py-2 border border-slate-300 text-slate-600 rounded-lg hover:bg-slate-50 transition"
                >
                  キャンセル
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <span className="text-lg font-semibold text-industrial-900">
                {organization?.name || '未設定'}
              </span>
              <button
                onClick={() => setIsEditingName(true)}
                className="text-sm text-safety-600 hover:text-safety-700 font-medium transition"
              >
                編集
              </button>
            </div>
          )}
        </div>
      </div>

      {/* メンバー管理 */}
      <MembersSection />

      {/* 工事現場 */}
      <WorkSitesSection />

      {/* プラン設定 */}
      <div>
        <h2 className="text-2xl font-bold text-industrial-900 mb-2">プラン</h2>
        <p className="text-sm text-slate-600 mb-6">
          管理画面を使う人数（席）で決まります。
          <strong className="text-industrial-900">報告する作業員はアカウント不要・人数無制限</strong>です。
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Free */}
          <div className={`rounded-xl border-2 p-5 ${
            currentPlan === 'free' ? 'border-safety-500 bg-safety-50/40' : 'border-slate-200 bg-white'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-industrial-900">フリー</span>
              {currentPlan === 'free' && (
                <span className="text-xs px-2 py-1 rounded-full bg-safety-500 text-white font-bold">
                  現在のプラン
                </span>
              )}
            </div>
            <p className="text-3xl font-extrabold text-industrial-900 mb-4">¥0<span className="text-sm font-normal text-slate-500">/月</span></p>
            <ul className="text-sm text-slate-600 space-y-1">
              <li>管理者 1人</li>
              <li>QRコード 3個</li>
              <li>AI分析 10回/月</li>
            </ul>
          </div>

          {/* 有料（席数制） */}
          <div className={`rounded-xl border-2 p-5 ${
            currentPlan !== 'free' ? 'border-safety-500 bg-safety-50/40' : 'border-slate-200 bg-white'
          }`}>
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-industrial-900">チーム</span>
              {currentPlan !== 'free' && (
                <span className="text-xs px-2 py-1 rounded-full bg-safety-500 text-white font-bold">
                  現在のプラン
                </span>
              )}
            </div>
            {/* 契約中は「いまの席数」を示すカード、未契約は「申し込む席数を選ぶ」カード。
                以前は契約中もセレクタが動き、金額と上限だけが変わった。
                「現在のプラン」と書いた札の下で架空の数字が出るため、
                何が現状なのか分からなくなっていた。 */}
            <p className="text-3xl font-extrabold text-industrial-900 mb-1">
              ¥{priceForSeats(shownSeats).toLocaleString()}
              <span className="text-sm font-normal text-slate-500">/月</span>
            </p>
            <p className="text-xs text-slate-500 mb-4">
              1席目 ¥{PLAN_CONFIGS.light.price.toLocaleString()} ＋ 追加 {Math.max(shownSeats - 1, 0)}席 × ¥{SEAT_PRICE.toLocaleString()}
            </p>

            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm text-slate-600">管理者</span>
              {currentPlan === 'free' ? (
                <select
                  value={seatCount}
                  onChange={(e) => setSeatCount(Number(e.target.value))}
                  className="px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                >
                  {Array.from({ length: MAX_SELF_SERVE_SEATS }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n}人</option>
                  ))}
                </select>
              ) : (
                <span className="text-sm font-bold text-industrial-900">{shownSeats}人</span>
              )}
            </div>

            <ul className="text-sm text-slate-600 space-y-1 mb-4">
              <li>QRコード {10 * shownSeats}個</li>
              <li>AI分析 {100 * shownSeats}回/月</li>
              <li>音声入力 {50 * shownSeats}回/月</li>
            </ul>

            {currentPlan === 'free' ? (
              <button
                onClick={() => handlePlanChange('light', seatCount)}
                disabled={loadingPlan !== null}
                className="w-full py-2.5 bg-safety-500 hover:bg-safety-600 disabled:opacity-50 text-white font-bold rounded-lg text-sm"
              >
                {loadingPlan ? '処理中...' : `このプランに変更（${seatCount}人）`}
              </button>
            ) : (
              endsOn ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <p className="font-bold">解約のお手続きが完了しています</p>
                  <p className="mt-1">
                    {new Date(endsOn).toLocaleDateString('ja-JP', { year: 'numeric', month: 'long', day: 'numeric' })}
                    {' '}まではチームプランをご利用いただけます。以降はフリープラン
                    （管理者1人・QRコード3個・AI分析10回/月）に戻ります。
                  </p>
                  <p className="mt-1">
                    上限を超えるQRコードは、フリープランに戻った時点で無効になります。
                    続けてご利用になる場合は、下の「Stripe顧客ポータルを開く」から解約を取り消せます。
                  </p>
                </div>
              ) : (
                <p className="text-xs text-slate-500 text-center">
                  席数の増減は、上の「メンバー」欄の<strong>「席数を変更」</strong>から行えます
                  （日割りで次回の請求に反映され、カードの再入力は不要です）
                </p>
              )
            )}
          </div>
        </div>

        {currentPlan !== 'free' && (
          <button
            onClick={() => handlePlanChange('free')}
            className="mt-4 text-sm text-slate-500 hover:text-slate-700 underline underline-offset-2"
          >
            契約内容の確認・解約
          </button>
        )}
      </div>

      {/* お支払い管理(Stripe顧客ポータル)はフリープランでは非表示。
          ポータルは stripe_customer_id を持つ契約者専用で、未契約者が押すと404になるため。 */}
      {isStripeConfigured() && currentPlan !== 'free' && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-industrial-900 mb-4 flex items-center">
            <CreditCard size={20} className="mr-2 text-slate-600" />
            お支払い管理
          </h3>
          <p className="text-sm text-slate-600 mb-4">
            支払い方法の変更、請求履歴の確認はStripe顧客ポータルから行えます。
          </p>
          <button
            onClick={() => openCustomerPortal(`${window.location.origin}/manager/settings`)}
            className="w-full py-3 bg-slate-100 text-industrial-900 font-medium rounded-lg hover:bg-slate-200 transition flex items-center justify-center"
          >
            <span>Stripe顧客ポータルを開く</span>
            <ExternalLink size={14} className="ml-2" />
          </button>
        </div>
      )}
    </div>
  );
};

// ============ Analytics View ============

import { getAnalyticsData, AnalyticsData } from '../services/supabase';
import { readCheckoutReturn, clearCheckoutReturn } from '../services/checkoutReturn';
import {
  BarChart3,
  Users,
  Building,
  QrCode as QrCodeIcon,
  FileText,
  CreditCard as CreditCardIcon,
  TrendingUp,
  Activity,
  RefreshCw
} from 'lucide-react';

export const ManagerAnalyticsView: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalytics = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getAnalyticsData();
      if (data) {
        setAnalytics(data);
      } else {
        setError('データの取得に失敗しました');
      }
    } catch (e) {
      setError('データの取得中にエラーが発生しました');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('ja-JP', {
        timeZone: 'Asia/Tokyo',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const getUsageLabel = (type: string) => {
    switch (type) {
      case 'ai_analysis': return 'AI分析';
      case 'audio_transcription': return '音声認識';
      case 'qr_scan': return 'QRスキャン';
      default: return type;
    }
  };

  const getRiskColor = (level: string) => {
    switch (level) {
      case 'High': return 'bg-red-100 text-red-800';
      case 'Medium': return 'bg-yellow-100 text-yellow-800';
      case 'Low': return 'bg-green-100 text-green-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getPlanLabel = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'エンタープライズ';
      case 'standard': return 'スタンダード';
      case 'light': return 'チーム';
      case 'free': return 'フリー';
      default: return plan;
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-500"></div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-800 font-medium">{error || 'データがありません'}</p>
        <button
          onClick={loadAnalytics}
          className="mt-4 px-4 py-2 bg-red-100 text-red-800 rounded-lg hover:bg-red-200 transition"
        >
          再読み込み
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-industrial-900">📊 統計・分析</h2>
          <p className="text-sm text-slate-600 mt-1">アプリ全体の使用状況を確認できます</p>
        </div>
        <button
          onClick={loadAnalytics}
          className="flex items-center px-4 py-2 bg-safety-500 text-white rounded-lg hover:bg-safety-600 transition"
        >
          <RefreshCw size={16} className="mr-2" />
          更新
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <Building size={16} className="mr-2" />
            <span className="text-xs">組織数</span>
          </div>
          <p className="text-2xl font-bold text-industrial-900">{analytics.summary.organizationCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <Users size={16} className="mr-2" />
            <span className="text-xs">ユーザー数</span>
          </div>
          <p className="text-2xl font-bold text-industrial-900">{analytics.summary.userCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <QrCodeIcon size={16} className="mr-2" />
            <span className="text-xs">QRポイント</span>
          </div>
          <p className="text-2xl font-bold text-industrial-900">{analytics.summary.qrPointCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <FileText size={16} className="mr-2" />
            <span className="text-xs">報告数</span>
          </div>
          <p className="text-2xl font-bold text-industrial-900">{analytics.summary.caseCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <CreditCardIcon size={16} className="mr-2" />
            <span className="text-xs">有料プラン</span>
          </div>
          <p className="text-2xl font-bold text-safety-600">{analytics.summary.paidSubscriptionCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
          <div className="flex items-center text-slate-500 mb-2">
            <Users size={16} className="mr-2" />
            <span className="text-xs">工事現場</span>
          </div>
          <p className="text-2xl font-bold text-industrial-900">{analytics.summary.teamCount}</p>
        </div>
      </div>

      {/* Usage & Plan Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Feature Usage */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-industrial-900 mb-4 flex items-center">
            <Activity size={20} className="mr-2 text-safety-500" />
            機能別使用回数
          </h3>
          {analytics.usageByType.length === 0 ? (
            <p className="text-slate-500 text-sm">データがありません</p>
          ) : (
            <div className="space-y-3">
              {analytics.usageByType.map((usage, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{getUsageLabel(usage.counterType)}</span>
                  <span className="text-xl font-bold text-industrial-900">{usage.totalCount.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Plan Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-industrial-900 mb-4 flex items-center">
            <BarChart3 size={20} className="mr-2 text-safety-500" />
            プラン別組織数
          </h3>
          {analytics.planDistribution.length === 0 ? (
            <p className="text-slate-500 text-sm">データがありません</p>
          ) : (
            <div className="space-y-3">
              {analytics.planDistribution.map((plan, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{getPlanLabel(plan.plan)}</span>
                  <span className="text-xl font-bold text-industrial-900">{plan.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Category & Risk Distribution */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Category Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-industrial-900 mb-4">4Mカテゴリ別</h3>
          {analytics.categoryDistribution.length === 0 ? (
            <p className="text-slate-500 text-sm">データがありません</p>
          ) : (
            <div className="space-y-3">
              {analytics.categoryDistribution.map((cat, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="font-medium text-slate-700">{cat.category}</span>
                  <span className="text-xl font-bold text-industrial-900">{cat.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Risk Distribution */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <h3 className="text-lg font-bold text-industrial-900 mb-4">リスクレベル別</h3>
          {analytics.riskDistribution.length === 0 ? (
            <p className="text-slate-500 text-sm">データがありません</p>
          ) : (
            <div className="space-y-3">
              {analytics.riskDistribution.map((risk, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRiskColor(risk.riskLevel)}`}>
                    {risk.riskLevel}
                  </span>
                  <div className="text-right">
                    <span className="text-xl font-bold text-industrial-900">{risk.count}</span>
                    <span className="text-sm text-slate-500 ml-2">({risk.percentage}%)</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* QR Point Stats */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-industrial-900 mb-4 flex items-center">
          <QrCodeIcon size={20} className="mr-2 text-safety-500" />
          QRポイント別統計
        </h3>
        {analytics.qrPointStats.length === 0 ? (
          <p className="text-slate-500 text-sm">データがありません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">名前</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">スキャン</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">投稿</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">最終スキャン</th>
                </tr>
              </thead>
              <tbody>
                {analytics.qrPointStats.map((qr, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-industrial-900">{qr.name}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{qr.scans}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{qr.posts}</td>
                    <td className="py-3 px-4 text-right text-sm text-slate-500">{formatDate(qr.lastScan)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Daily Posts */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <h3 className="text-lg font-bold text-industrial-900 mb-4 flex items-center">
          <TrendingUp size={20} className="mr-2 text-safety-500" />
          日別投稿数（過去30日）
        </h3>
        {analytics.dailyPosts.length === 0 ? (
          <p className="text-slate-500 text-sm">データがありません</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600">日付</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600">投稿数</th>
                </tr>
              </thead>
              <tbody>
                {analytics.dailyPosts.map((day, idx) => (
                  <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-3 px-4 font-medium text-industrial-900">{day.date}</td>
                    <td className="py-3 px-4 text-right text-slate-700">{day.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
